// Data Encryption Utilities
import CryptoJS from 'crypto-js';
import logger from './logger';

// Generate a secure key from environment or throw error
const getEncryptionKey = () => {
    const key = process.env.REACT_APP_ENCRYPTION_KEY;
    if (!key || key === 'dev-key-change-in-production' || key.includes('your_')) {
        throw new Error('ENCRYPTION_KEY not configured. Set REACT_APP_ENCRYPTION_KEY in environment.');
    }
    return key;
};

export const encryptionUtils = {
    // Hash sensitive data for logs using SHA-256
    hashForLogs: (data) => {
        if (!data) return 'unknown';
        return CryptoJS.SHA256(data.toString()).toString().substring(0, 8);
    },

    // Mask sensitive data for display
    maskData: (data, visibleChars = 4) => {
        if (!data || data.length <= visibleChars) return data;
        const visible = data.substring(0, visibleChars);
        const masked = '*'.repeat(data.length - visibleChars);
        return visible + masked;
    },

    // Secure encryption using AES
    encrypt: (data) => {
        if (!data) return data;
        try {
            const key = getEncryptionKey();
            const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), key).toString();
            return encrypted;
        } catch (error) {
            logger.error('Encryption failed:', error);
            return null;
        }
    },

    // Secure decryption using AES
    decrypt: (encryptedData) => {
        if (!encryptedData) return encryptedData;
        try {
            const key = getEncryptionKey();
            const decrypted = CryptoJS.AES.decrypt(encryptedData, key);
            const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);

            // Validate decrypted string before JSON parsing
            if (!decryptedString || decryptedString.trim() === '') {
                logger.warn('Decryption produced empty result');
                return null;
            }

            return JSON.parse(decryptedString);
        } catch (error) {
            logger.error('Decryption failed:', error);
            return null;
        }
    },

    // Validate phone number format (international)
    isValidPhone: (phone) => {
        const phoneRegex = /^[+]?[1-9][\d]{7,15}$/;
        return phoneRegex.test(phone?.replace(/[\s\-()]/g, ''));
    },

    // Validate passport number format
    isValidPassport: (passport) => {
        const passportRegex = /^[A-Z0-9]{6,12}$/;
        return passportRegex.test(passport?.toUpperCase());
    },

    // Generate secure random token
    generateSecureToken: (length = 32) => {
        return CryptoJS.lib.WordArray.random(length).toString();
    },

    // Sanitize input to prevent XSS (optimized)
    sanitizeInput: (input) => {
        if (typeof input !== 'string') return input;

        // Use single replace with regex for better performance
        return input.replace(/[<>"'/]/g, (match) => {
            const replacements = {
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                "'": '&#x27;',
                '/': '&#x2F;'
            };
            return replacements[match];
        }).substring(0, 1000); // Limit length
    }
};